import { useEffect, useMemo, useState } from "react";
import ModalComponent from "components/modal";
import {
  IconCheck,
  IconDownload,
  IconEye,
  IconFileText,
  IconFileTypeHtml,
  IconPencil,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import { useSharedState } from "state";
import { useParams } from "react-router-dom";
import {
  addCommentToPayment,
  approvePaymentRequest,
  deletePayment,
  getCommentsForPayment,
  getPaymentApprovals,
  getPaymentById,
  getPaymentDetails,
  getPaymentFileLink,
  getPaymentFileNames,
  getPaymentHtmlPreview,
  markPaymentTodoItemAsChecked,
  markPaymentTodoItemAsUnchecked,
  previewPaymentDocFile,
  restartPaymentRequest,
  uploadPaymentFiles,
} from "api/payment";
import moment from "moment";
import DangerModal from "components/modal/danger";
import WarningModal from "components/modal/warning";
import { LevelType, StatusEnum } from "types/dict";
import { CommentProps } from "types/comment";
import getUserInitials from "helpers/get-user-initials";
import { FileDetails, FilesResponse } from "types/file";
import compactNumber from "helpers/compact-number";
import { PaymentProps } from "types/payment";
import ForbiddenPage from "components/protected-route/forbidden";
import { tagsList } from "components/table/table-parts";
import { getBudgetBalance } from "api/budget_request";
import { getMimeType } from "helpers/get-mime-type";
import { PreviewFile } from "helpers/preview-file";
import StatusList from "components/details/status-list";
import NewPayment from "../new";

export interface ExpenseItem {
  name: string;
  quantity: number | undefined;
  price: number | undefined;
  sum: number | undefined;
  expense_item: string;
  budget?: number;
  budget_levels?: number[];
  budget_balance?: number;
}

export const emptyExpenseRow: ExpenseItem = {
  name: "",
  quantity: undefined,
  price: undefined,
  sum: undefined,
  expense_item: "",
  budget: undefined,
  budget_levels: undefined,
  budget_balance: undefined,
};

export interface StatusProps {
  name: string;
  position: string;
  status: number;
}

export default function PaymentDetail() {
  const {
    user,
    payments,
    getPaymentsData,
    getPaymentsTableData,
    companies,
    getCompaniesData,
    departments,
    getDepartmentsData,
    isAdmin,
    setCopyData,
    notify,
  } = useSharedState();
  const { paymentId, locationName } = useParams();

  const [paymentItem, setPaymentItem] = useState<PaymentProps | undefined>(undefined);
  const [statuses, setStatuses] = useState<StatusProps[]>([]);
  const [expenseTable, setExpenseTable] = useState<ExpenseItem[]>([]);
  const [newInfo, setNewInfo] = useState<string>("");

  const [isForbidden, setForbidden] = useState<boolean>(false);
  const [modalTab, setModalTab] = useState<"info" | "journal">("info");

  const [files, setFiles] = useState<FilesResponse | undefined>(undefined);
  const [comments, setComments] = useState<CommentProps[] | undefined>(undefined);
  const [isFileLoading, setFileLoading] = useState<boolean>(false);

  const [newComment, setNewComment] = useState<string>("");
  const [isLeavingComment, setLeavingComment] = useState<boolean>(false);

  const [isNewInfoEditing, setNewInfoEditing] = useState<boolean>(false);
  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const [showCopyModal, setShowCopyModal] = useState<boolean>(false);

  const deletePaymentDanger = "Вы действительно хотите удалить данную заявку?";
  const rejectPayment = "Вы действительно хотите отклонить данную заявку?";
  const approvePayment = `Вы действительно хотите утвердить данную заявку${
    newInfo ? " с введённым дополнительным номером" : ""
  }?`;
  const restartPayment = "Вы действительно хотите перезапустить данную заявку?";

  const budgetReady = useMemo(() => {
    return !!paymentItem?.company_id && !!paymentItem?.department_id && !!paymentItem?.year && !!expenseTable.length;
  }, [paymentItem, expenseTable]);

  useEffect(() => {
    initPayment();
  }, [paymentId]);

  useEffect(() => {
    budgetReady && handleGetBudgetBalance();
  }, [budgetReady]);

  const initPayment = async () => {
    try {
      setFormLoading(true);

      getPaymentData();
      getPaymentDetailsData();
      getStatuses();
      getFilesData();
      getCommentsData();
      getCompaniesData();
      getDepartmentsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      if (error?.response?.status === 403) {
        notify(err, "danger", "Отказано в доступе");
        setForbidden(true);
      }
    } finally {
      setFormLoading(false);
    }
  };

  const getPaymentData = async () => {
    try {
      const res: PaymentProps = await getPaymentById(Number(paymentId));
      setPaymentItem(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении заявки");
      throw error;
    }
  };

  const getPaymentDetailsData = async () => {
    try {
      const details = await getPaymentDetails(paymentId ? +paymentId : 0);
      const formattedDetails: ExpenseItem[] = [];

      details.forEach((detail: any) => {
        let item: ExpenseItem = {
          name: detail["Наименование товара"],
          quantity: detail["Количество"],
          price: detail["Цена"],
          sum: detail["Сумма"],
          expense_item: detail.budget_levels?.length ? undefined : detail["Статья из бюджета/Вне бюджета"],
          budget_levels: detail.budget_levels?.length ? detail.budget_levels : undefined,
        };
        formattedDetails.push(item);
      });

      setExpenseTable(formattedDetails);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении деталей");
    }
  };

  const getStatuses = async () => {
    try {
      const approvals: StatusProps[] = await getPaymentApprovals(paymentId ? +paymentId : 0);
      setStatuses(approvals);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении статусов");
    }
  };

  const getFilesData = async () => {
    try {
      const res: FilesResponse = await getPaymentFileNames(Number(paymentId));
      setFiles(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении файлов");
    }
  };

  const getCommentsData = async () => {
    try {
      const res: any = await getCommentsForPayment(Number(paymentId));
      setComments(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении комментариев");
    }
  };

  const uploadFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const formData = new FormData();

      if (e.target.files?.length) {
        Array.from(e.target.files).forEach((file) => {
          formData.append(`files`, file);
        });
      }

      await uploadPaymentFiles(Number(paymentId), formData);

      notify("Файл добавлен", "success");
      getFilesData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при добавлении файла");
    } finally {
      setFormLoading(false);
    }
  };

  const onFileAction = async (file: FileDetails, action: "html_preview" | "preview" | "download") => {
    setFileLoading(true);
    try {
      if (action === "html_preview") {
        const res = await getPaymentHtmlPreview(Number(paymentId), file.file_name);
        PreviewFile(res, "preview");
      } else if (file.file_name.includes(".doc") && action === "preview") {
        const res = await previewPaymentDocFile(Number(paymentId), file.file_name);
        PreviewFile(res, action);
      } else {
        const res = await getPaymentFileLink(Number(paymentId), file);
        PreviewFile(res, action);
      }
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении ссылки");
    } finally {
      setFileLoading(false);
    }
  };

  const cancelNewInfoEdit = () => {
    setNewInfoEditing(false);
    setNewInfo("");
  };

  const openNewInfoEdit = () => {
    setNewInfoEditing(true);
    setNewInfo(paymentItem?.additional_info || "");
  };

  const openPaymentModal = () => {
    const button = document.createElement("button");
    button.setAttribute("data-bs-toggle", "modal");
    button.setAttribute("data-bs-target", "#modal");
    document.body.appendChild(button);
    button.click();
    button.remove();
  };

  const sortedStatuses = useMemo(() => {
    const completedStatuses = [...statuses].filter((s) => s.status === 2);
    const rejectedStatuses = [...statuses].filter((s) => s.status === 3);
    const currentStatuses = [...statuses].filter((s) => s.status === 1);
    const nullStatuses = [...statuses].filter((s) => s.status === null);

    return [...completedStatuses, ...rejectedStatuses, ...currentStatuses, ...nullStatuses];
  }, [statuses]);

  const isPaymentActive = useMemo(() => {
    return paymentItem && paymentItem.status !== StatusEnum.COMPLETED && paymentItem.status !== StatusEnum.REJECTED
      ? true
      : false;
  }, [paymentItem]);

  const isPaymentDeleteable = useMemo(() => {
    return paymentItem && paymentItem.status !== StatusEnum.COMPLETED && isAdmin ? true : false;
  }, [paymentItem, isAdmin]);

  const isPaymentApprovable = useMemo(() => {
    if (paymentItem && paymentItem.pending_approver_id === user.result.user_id && isPaymentActive) return true;
    return false;
  }, [paymentItem, user, isPaymentActive]);

  const isPaymentExecuting = useMemo(() => {
    if (paymentItem && paymentItem.pending_action_type === "execute" && paymentItem.status === StatusEnum.IN_PROGRESS)
      return true;
    return false;
  }, [paymentItem]);

  const isPaymentCopyable = useMemo(() => {
    return paymentItem && paymentItem.status === StatusEnum.COMPLETED;
  }, [paymentItem]);

  const isPaymentRestartable = useMemo(() => {
    return paymentItem && paymentItem.user_id === user.result.user_id && paymentItem.status === StatusEnum.REJECTED;
  }, [paymentItem, user]);

  const isFooterVisible = useMemo(() => {
    return isPaymentDeleteable || isPaymentApprovable || isPaymentCopyable || isPaymentRestartable;
  }, [isPaymentDeleteable, isPaymentApprovable, isPaymentCopyable, isPaymentRestartable]);

  const isChecklistAvailable = useMemo(() => {
    return (
      paymentItem &&
      (paymentItem.status === StatusEnum.COMPLETED || paymentItem.pending_action_type === "execute") &&
      paymentItem.todo_items.length
    );
  }, [paymentItem]);

  const isExecutingDisabled = useMemo(() => {
    return (
      paymentItem &&
      paymentItem.user_id === user.result.user_id &&
      paymentItem.pending_action_type === "execute" &&
      paymentItem.todo_items.some((item) => !!!item.is_completed)
    );
  }, [paymentItem, user]);

  const cancelCommentLeave = () => {
    setLeavingComment(false);
    setNewComment("");
  };

  const changeTab = (tab: "info" | "journal") => {
    setModalTab(tab);
    cancelCommentLeave();
  };

  const handleGetBudgetBalance = async () => {
    try {
      setFormLoading(true);

      // Create a map to store unique budget level combinations and their initial balances
      const budgetLevelMap = new Map<string, number>();

      // Build a new expense table with updated budget balances
      const updatedExpenseTable: ExpenseItem[] = [];

      for (let i = 0; i < expenseTable.length; i++) {
        const expense = expenseTable[i];

        if (!expense.budget_levels?.length) {
          updatedExpenseTable.push({ ...expense });
          continue;
        }

        const levelIdsArray = [...(expense.budget_levels as unknown as LevelType[]).map((b) => b.budget_type_id!)];
        const levelKey = levelIdsArray.join("-");

        let budget_balance: number | undefined = undefined;

        if (budgetLevelMap.has(levelKey)) {
          // Use the remaining balance from previous calculation
          budget_balance = budgetLevelMap.get(levelKey);
        } else {
          // Fetch the balance from API
          const balanceData = {
            company_id: paymentItem?.company_id,
            department_id: paymentItem?.department_id,
            year: paymentItem?.year,
            budget_levels: [...levelIdsArray],
          };

          const res: { remaining_budget_balance: number } = await getBudgetBalance(balanceData);
          budget_balance = res.remaining_budget_balance;
        }

        // Calculate new remaining balance for next row with the same key
        const newBalance = (budget_balance || 0) - (expense.quantity || 0) * (expense.price || 0);
        budgetLevelMap.set(levelKey, newBalance);

        updatedExpenseTable.push({
          ...expense,
          budget_balance,
        });
      }

      setExpenseTable(updatedExpenseTable);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при расчёте бюджета");
    } finally {
      setFormLoading(false);
    }
  };

  const onCheckChange = async (todoItemId: number, check: boolean) => {
    try {
      setFormLoading(true);

      switch (check) {
        case true:
          await markPaymentTodoItemAsChecked(todoItemId);
          break;

        case false:
          await markPaymentTodoItemAsUnchecked(todoItemId);
          break;

        default:
          break;
      }

      notify("Чек-лист отмечен", "success");
      getPaymentData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при отметке чек-листа");
    } finally {
      setFormLoading(false);
    }
  };

  const onPaymentDelete = async () => {
    openPaymentModal();
    setFormLoading(true);

    try {
      await deletePayment(paymentItem?.payment_request_id || 0);

      setFormSubmitted(true);
      const filters = new URLSearchParams();
      filters.append("locations", String(locationName));
      await getPaymentsData(undefined, filters);
      await getPaymentsTableData(undefined, filters);
      notify("Заявка удалена", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при удалении заявки");
    } finally {
      setFormLoading(false);
    }
  };

  const onPaymentSubmit = async (approved: boolean) => {
    openPaymentModal();
    setFormLoading(true);

    try {
      await approvePaymentRequest(paymentItem?.payment_request_id || 0, approved, newInfo);

      await initPayment();
      const filters = new URLSearchParams();
      filters.append("locations", String(locationName));
      await getPaymentsData(undefined, filters);
      await getPaymentsTableData(undefined, filters);
      notify(approved ? "Заявка утверждена" : "Заявка отклонена", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", approved ? "Ошибка при утверждении заявки" : "Ошибка при отклонении заявки");
    } finally {
      setFormLoading(false);
    }
  };

  const expenseTableHeaders = [
    { header: "Наименование", narrow: false, wrap: false },
    { header: "Количество", narrow: false, wrap: true },
    { header: "Цена", narrow: false, wrap: false },
    { header: "Сумма", narrow: false, wrap: false },
    { header: "Остаток бюждета", narrow: false, wrap: false },
    { header: "Статья из бюджета/вне бюджета", narrow: false, wrap: false },
  ];

  const onCommentLeave = async () => {
    setFormLoading(true);

    try {
      await addCommentToPayment(Number(paymentId) || 0, newComment);

      cancelCommentLeave();
      await getCommentsData();
      notify("Комментарий сохранён", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при сохранении комментария");
    } finally {
      setFormLoading(false);
    }
  };

  const onPaymentCopy = () => {
    if (paymentItem) {
      setCopyData(paymentItem);
      setShowCopyModal(true);
    }
  };

  const onPaymentRestart = async () => {
    openPaymentModal();
    setFormLoading(true);

    try {
      await restartPaymentRequest(Number(paymentId) || 0);

      await initPayment();
      const filters = new URLSearchParams();
      filters.append("locations", String(locationName));
      await getPaymentsData(undefined, filters);
      await getPaymentsTableData(undefined, filters);
      notify("Заявка перезапущена", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при перезапуске заявки");
    } finally {
      setFormLoading(false);
    }
  };

  const detailsHeader = (
    <ul className="nav nav-tabs card-header-tabs nav-fill" data-bs-toggle="tabs" role="tablist">
      <li className="nav-item" role="presentation">
        <div
          className="nav-link active cursor-pointer"
          style={{ "--tblr-card-bg": "white" } as React.CSSProperties}
          data-bs-toggle="tab"
          aria-selected="true"
          role="tab"
          onClick={() => changeTab("info")}
        >
          Детали оплаты
        </div>
      </li>
      <li className="nav-item" role="presentation">
        <div
          className="nav-link cursor-pointer"
          style={{ "--tblr-card-bg": "white" } as React.CSSProperties}
          data-bs-toggle="tab"
          aria-selected="false"
          role="tab"
          onClick={() => changeTab("journal")}
        >
          Журнал
        </div>
      </li>
    </ul>
  );

  const detailsBody = (
    <div className="row">
      <div className="col-lg-3 mb-4">
        <StatusList statusList={statuses} />

        {isChecklistAvailable && (
          <div className="px-4 py-2">
            <h3 className="datagrid-title mb-2">Чек-лист</h3>
            <div className="card-body mb-3">
              {paymentItem?.todo_items.map((todo, index) => (
                <label className="form-check" key={index}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={todo.is_completed}
                    onChange={(e) => onCheckChange(todo.id, e.target.checked)}
                    disabled={isFormLoading || !isPaymentApprovable || paymentItem.user_id !== user.result.user_id}
                  />
                  <span className="form-check-label">{todo.title}</span>
                </label>
              ))}
            </div>

            {isPaymentApprovable && paymentItem?.user_id === user.result.user_id && (
              <div className="alert alert-primary m-0" style={{ padding: "7px 16px" }} role="alert">
                <div className="text-secondary">Для исполнения заявки отметьте весь чек-лист к исполнению</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="col-lg-9 mb-4">
        <div className="datagrid p-4 rounded-2xl border border-[#EFEFEF]">
          <div className="datagrid-item">
            <div className="datagrid-title">Юр. лицо</div>
            <div className="datagrid-content">{paymentItem?.company_name || "-"}</div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">Филиал</div>
            <div className="datagrid-content">{paymentItem?.choosen_location || "-"}</div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">Отдел</div>
            <div className="datagrid-content">{paymentItem?.department_name || "-"}</div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">Проект</div>
            <div className="datagrid-content">{paymentItem?.project || "-"}</div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">Контрагент</div>
            <div className="datagrid-content">{paymentItem?.contragent || "-"}</div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">Номер договора</div>
            <div className="datagrid-content">
              {paymentItem?.document_request_id ? (
                <a
                  className="body-1-semibold cursor-pointer underline text-blue-bold hover:opacity-70"
                  onClick={() =>
                    window.open(
                      `/documents/${paymentItem.choosen_location}/${paymentItem.document_request_id}`,
                      "_blank"
                    )
                  }
                >
                  №{paymentItem.dogovor}
                </a>
              ) : (
                paymentItem?.dogovor || "-"
              )}
            </div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">Наименование</div>
            <div className="datagrid-content">{paymentItem?.title || "-"}</div>
          </div>

          <div className="datagrid-item">
            <div className="datagrid-title">Описание расхода</div>
            <div className="datagrid-content">{paymentItem?.opisanie_rasxoda || "-"}</div>
          </div>

          <div className="datagrid-item">
            <div className="datagrid-title">Дополнительная информация</div>
            <div className="datagrid-content">
              {isPaymentApprovable && user.result.access_to_additional_info && !paymentItem?.additional_info ? (
                isNewInfoEditing ? (
                  <div className="d-flex items-center gap-2">
                    <input
                      type="text"
                      className="form-control"
                      value={newInfo || paymentItem?.additional_info}
                      onChange={(e) => setNewInfo(e.target.value)}
                    />

                    <button type="button" className="btn btn-icon" onClick={cancelNewInfoEdit}>
                      <IconX className="text-danger icon" />
                    </button>

                    <button type="button" className="btn btn-icon" onClick={() => setNewInfoEditing(false)}>
                      <IconCheck className="text-primary icon" />
                    </button>
                  </div>
                ) : newInfo || paymentItem?.additional_info ? (
                  <>
                    {newInfo || paymentItem?.additional_info}
                    <button type="button" className="btn btn-icon ms-1" onClick={openNewInfoEdit}>
                      <IconPencil className="text-primary icon" />
                    </button>
                  </>
                ) : (
                  <button type="button" className="btn text-primary" onClick={openNewInfoEdit}>
                    <IconPlus className="icon" />
                    Добавить информацию
                  </button>
                )
              ) : (
                paymentItem?.additional_info || "-"
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 mb-3 rounded-2xl border border-[#EFEFEF]">
        <div className="flex justify-between mb-3 datagrid-title font-semibold text-[#18181B]">
          Файлы
          {isFooterVisible && (
            <label
              htmlFor="new_file"
              className="flex items-center gap-1 pl-3 pr-[10px] py-1 rounded-lg text-sm font-semibold text-[#004369] bg-[#E5ECF0] cursor-pointer"
            >
              Добавить <IconPlus />
              <input type="file" id="new_file" onChange={uploadFiles} multiple hidden />
            </label>
          )}
        </div>

        {files?.file_details.length ? (
          <div className="flex flex-col gap-3">
            {files.file_details.map((file, index) => (
              <div key={index} className="flex items-center gap-3 p-4 rounded-2xl border border-[#EFEFEF]">
                <div className="flex flex-col items-center justify-center w-11 h-11 rounded-lg bg-[#E5ECF0]">
                  <IconFileText />
                </div>

                <div className="flex items-center justify-between gap-3 grow">
                  <div className="d-block text-truncate mt-n1" title={file.file_name}>
                    {file.file_name}
                  </div>

                  <div className="d-block text-truncate mt-n1">
                    {moment(file.saved_datetime).add(5, "hours").format("DD.MM.YY HH:mm") || "-"}
                  </div>
                </div>

                <div className="col-auto btn-list flex-nowrap">
                  {!!file.file_name.includes(".doc") && (
                    <button
                      type="button"
                      title="Просмотреть"
                      className="btn btn-icon btn-ghost-primary d-flex gap-2"
                      onClick={() => onFileAction(file, "html_preview")}
                      disabled={isFileLoading}
                    >
                      {isFileLoading ? (
                        <div className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <IconFileTypeHtml className="icon" />
                      )}
                    </button>
                  )}
                  {!!!file.file_name.includes(".xls") && !!!file.file_name.includes(".csv") && (
                    <button
                      type="button"
                      title="Просмотреть"
                      className="btn btn-icon btn-ghost-primary d-flex gap-2"
                      onClick={() => onFileAction(file, "preview")}
                      disabled={isFileLoading}
                    >
                      {isFileLoading ? (
                        <div className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <IconEye className="icon" />
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    title="Скачать"
                    className="btn btn-icon btn-ghost-primary d-flex gap-2"
                    onClick={() => onFileAction(file, "download")}
                    disabled={isFileLoading}
                  >
                    {isFileLoading ? (
                      <div className="spinner-border spinner-border-sm" role="status" />
                    ) : (
                      <IconDownload className="icon" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>Нет файлов</div>
        )}
      </div>

      <div className="p-4 mb-4 rounded-2xl border border-[#EFEFEF]">
        <div className="flex justify-between mb-3 datagrid-title font-semibold text-[#18181B]">Детали оплаты</div>
        <div className="card rounded-2xl">
          <div className="table-responsive rounded-2xl">
            <table className="table table-vcenter card-table">
              <thead>
                <tr>
                  {expenseTableHeaders.map((header, index) => (
                    <th className={`${header.narrow && "w-1"} ${header.wrap && "text-wrap"}`} key={index}>
                      {header.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {expenseTable?.map((expense, index) => (
                  <tr key={index}>
                    <td>{expense.name}</td>
                    <td>{compactNumber(expense.quantity || 0) || "-"}</td>
                    <td>{compactNumber(expense.price || 0) || "-"}</td>
                    <td>{compactNumber(expense.sum || 0) || "-"}</td>
                    <td>{compactNumber((expense.budget_balance || 0) - (expense.sum || 0)) || "-"}</td>
                    <td>
                      {expense.budget_levels?.length
                        ? (expense.budget_levels as unknown as LevelType[]).map((b) => b.budget_type || "").join(" → ")
                        : expense.expense_item}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="datagrid p-4 rounded-2xl border border-[#EFEFEF]">
        <div className="datagrid-item">
          <div className="datagrid-title">Итоговая сумма</div>
          <div className="datagrid-content">{compactNumber(paymentItem?.total_sum || 0) || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Сумма бюджета</div>
          <div className="datagrid-content">{compactNumber(paymentItem?.budget_summa || 0) || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Остаток бюджета</div>
          <div className="datagrid-content">{compactNumber(paymentItem?.ostatok_budgeta || 0) || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Год бюджета</div>
          <div className="datagrid-content">{paymentItem?.year || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Вид оплаты</div>
          <div className="datagrid-content">{paymentItem?.vid_oplaty || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Шаблон</div>
          <div className="datagrid-content">{paymentItem?.template_name || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Создатель</div>
          <div className="datagrid-content">{paymentItem?.creator_name || "-"}</div>
        </div>
      </div>
    </div>
  );

  const journalBody = (
    <div className="row">
      <div className="col-lg-12">
        <div className="comment-list">
          {comments?.map((comment, index) => (
            <div className="comment-item" key={index}>
              <div className="comment-body">
                <div className="flex items-center gap-3">
                  <div className="user-avatar flex items-center justify-center w-10 h-10 text-sm leading-4 font-medium rounded-full bg-[#FAFAFA]">
                    {getUserInitials(comment.user_name) || "-"}
                  </div>

                  <div className="flex flex-col gap-1">
                    <div className="text-sm leading-[21px] font-semibold text-[#18181B]">{comment.user_name}</div>
                    <div className="text-xs leading-[18px] font-medium text-[#18181B]">
                      {moment(comment.comment_date).format("DD.MM.YYYY HH:mm")}
                    </div>
                  </div>
                </div>

                <div className="pl-[52px] text-sm leading-[21px] font-medium text-[#18181B]">{comment.logs}</div>
              </div>
              <div className="divider"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const detailsFooter = (
    <>
      {isPaymentCopyable && (
        <button className="btn btn-primary d-flex gap-2" onClick={onPaymentCopy} disabled={isFormLoading}>
          Копировать
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}

      {isPaymentRestartable && (
        <button
          className="btn btn-primary d-flex gap-2"
          data-bs-toggle="modal"
          data-bs-target="#modal-restart-payment"
          disabled={isFormLoading}
        >
          Перезапустить
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}

      {isPaymentDeleteable && (
        <button
          className="btn btn-ghost-danger d-flex gap-2"
          data-bs-toggle="modal"
          data-bs-target="#modal-delete-payment"
          disabled={isFormLoading || isNewInfoEditing}
        >
          Удалить
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}

      {isPaymentApprovable && (
        <>
          {!isPaymentExecuting && (
            <button
              className="btn btn-ghost-primary d-flex gap-2"
              data-bs-toggle="modal"
              data-bs-target="#modal-reject-payment"
              disabled={isFormLoading || isNewInfoEditing}
            >
              Отклонить
              {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
            </button>
          )}
          <button
            className="btn btn-primary d-flex gap-2"
            data-bs-toggle="modal"
            data-bs-target="#modal-approve-payment"
            disabled={isFormLoading || isNewInfoEditing || isExecutingDisabled}
          >
            Утвердить
            {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
          </button>
        </>
      )}
    </>
  );

  const journalFooter = (
    <>
      {isLeavingComment ? (
        <>
          <input
            className="form-control rounded-2xl"
            style={{ width: 0, flexGrow: 1 }}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && newComment && !isFormLoading && onCommentLeave()}
            disabled={isFormLoading}
          />

          <button className="btn btn-ghost-primary d-flex gap-2" onClick={cancelCommentLeave} disabled={isFormLoading}>
            Отмена
            {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
          </button>

          <button
            className="btn btn-primary d-flex gap-2"
            onClick={onCommentLeave}
            disabled={!newComment || isFormLoading}
          >
            Сохранить
            {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
          </button>
        </>
      ) : (
        <button
          className="btn btn-primary d-flex gap-2"
          onClick={() => setLeavingComment(true)}
          disabled={isFormLoading}
        >
          Добавить комментарий
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}
    </>
  );

  return (
    <>
      <ModalComponent
        header={detailsHeader}
        body={isForbidden ? <ForbiddenPage /> : modalTab === "info" ? detailsBody : journalBody}
        footer={
          isFooterVisible && modalTab === "info"
            ? detailsFooter
            : isFooterVisible && modalTab === "journal"
            ? journalFooter
            : undefined
        }
        alignHeaderToBottom
        disableClose={isFormLoading}
        shouldModalClose={isFormSubmitted || showCopyModal}
        shouldModalNavigate={
          showCopyModal
            ? `/payments/${paymentItem?.choosen_location}/create`
            : `/payments/${paymentItem?.choosen_location}`
        }
      />
      <DangerModal
        modal_id="modal-delete-payment"
        dangerDescription={deletePaymentDanger}
        cancel={openPaymentModal}
        confirm={onPaymentDelete}
      />
      <WarningModal
        modal_id="modal-reject-payment"
        warningDescription={rejectPayment}
        cancel={openPaymentModal}
        confirm={() => onPaymentSubmit(false)}
      />
      <WarningModal
        modal_id="modal-approve-payment"
        warningDescription={approvePayment}
        cancel={openPaymentModal}
        confirm={() => onPaymentSubmit(true)}
      />
      <WarningModal
        modal_id="modal-restart-payment"
        warningDescription={restartPayment}
        cancel={openPaymentModal}
        confirm={() => onPaymentRestart()}
      />
    </>
  );
}
